/**
 * The capture/enrichment tier context, split from the provider component that fills it.
 *
 * Same split as `layout/editModeContext.ts` and for the same reason: a module that exports
 * both a component and a hook defeats fast refresh, and oxlint says so with warnings-as-errors.
 */

import { createContext, useContext } from "react";
import type { CaptureTier } from "./api";

export type CaptureTierState = {
  /** `null` until the initial `GET /api/capture-tier` resolves — also what a mount with no
   * `CaptureTierProvider` looks like forever, which the default value below covers on
   * purpose: `CaptureTierPicker` sits inside `QuickAddCard`, rendered standalone (no full
   * app chrome) across a long list of existing tests unrelated to AI tiers, and a bulb
   * quietly showing "off" there is the right failure mode, not a thrown error at mount. */
  tier: CaptureTier | null;
  setTier: (tier: CaptureTier) => Promise<void>;
};

const UNMOUNTED: CaptureTierState = {
  tier: null,
  setTier: () => {
    throw new Error("No CaptureTierProvider is mounted");
  },
};

export const CaptureTierContext = createContext<CaptureTierState>(UNMOUNTED);

export function useCaptureTier(): CaptureTierState {
  return useContext(CaptureTierContext);
}
